/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useMemo, useState, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DataObjectIcon from '@mui/icons-material/DataObject';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MergeIcon from '@mui/icons-material/CallMerge';
import { toast } from 'react-toastify';

import {
  modelLabDatasetApi,
  modelLabDatasetVersionApi,
} from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import type {
  ModelLabDataset,
  ModelLabDatasetCreate,
  ModelLabDatasetSourceType,
  ModelLabDatasetVersionCreate,
  ModelLabDatasetVersion,
  DataProfile,
} from '../../types';
import {
  ConfirmDialog,
  ErrorAlert,
  LoadingSpinner,
  PageHeader,
} from '../common';
import { getApiErrorMessage, parseCommaList, parseJsonInput } from './utils';
import {
  CorrelationHeatmap,
  BoxPlotSummary,
  NumericDistribution,
  CategoricalDistribution,
} from './charts';

/* ---- Data input mode for create/version dialogs ---- */
type DataInputMode = 'file' | 'json' | 'market';

interface FilePreview {
  fileName: string;
  fileSize: number;
  columns: string[];
  rowCount: number;
  sampleRows: Record<string, any>[];
}

interface DatasetFormState {
  name: string;
  description: string;
  sourceType: ModelLabDatasetSourceType;
  tags: string;
  schemaJson: string;
  versionName: string;
  versionDescription: string;
  recordsJson: string;
  metadataJson: string;
}

interface DatasetVersionFormState {
  name: string;
  description: string;
  schemaJson: string;
  recordsJson: string;
  metadataJson: string;
}

const initialDatasetForm: DatasetFormState = {
  name: '',
  description: '',
  sourceType: 'MANUAL',
  tags: '',
  schemaJson: '{}',
  versionName: 'Seed v1',
  versionDescription: '',
  recordsJson: '[\n  {\n    "feature_1": 1.0,\n    "feature_2": 2.0,\n    "target": 0\n  }\n]',
  metadataJson: '{}',
};

const initialVersionForm: DatasetVersionFormState = {
  name: 'Next revision',
  description: '',
  schemaJson: '{}',
  recordsJson: '[]',
  metadataJson: '{}',
};

/* ── CSV parsing helper (client-side preview only) ── */
function parseCsvPreview(text: string, maxRows = 5): { columns: string[]; rows: Record<string, any>[]; totalRows: number } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { columns: [], rows: [], totalRows: 0 };
  const columns = lines[0].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
  const dataLines = lines.slice(1);
  const rows = dataLines.slice(0, maxRows).map((line) => {
    const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row: Record<string, any> = {};
    columns.forEach((col, i) => { row[col] = values[i] ?? ''; });
    return row;
  });
  return { columns, rows, totalRows: dataLines.length };
}

/* ── Drag-and-drop file zone ── */
const DropZone: React.FC<{
  onFile: (file: File) => void;
  file: File | null;
  accept: string;
  label: string;
}> = ({ onFile, file, accept, label }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFile(dropped);
  }, [onFile]);

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      sx={{
        border: '2px dashed',
        borderColor: dragOver ? 'primary.main' : file ? 'success.main' : 'divider',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        bgcolor: dragOver ? 'action.hover' : file ? 'success.50' : 'background.default',
        transition: 'all 0.2s',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }}
      />
      {file ? (
        <Stack alignItems="center" spacing={1}>
          <InsertDriveFileIcon color="success" sx={{ fontSize: 40 }} />
          <Typography variant="subtitle2">{file.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {(file.size / 1024).toFixed(1)} KB
          </Typography>
        </Stack>
      ) : (
        <Stack alignItems="center" spacing={1}>
          <CloudUploadIcon color="action" sx={{ fontSize: 40 }} />
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="caption" color="text.secondary">
            Drag & drop or click to browse
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

/* ── File preview table ── */
const FilePreviewTable: React.FC<{ preview: FilePreview | null; loading?: boolean }> = ({ preview, loading }) => {
  if (loading) return <LinearProgress />;
  if (!preview) return null;
  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Chip label={`${preview.rowCount} rows`} size="small" color="info" />
          <Chip label={`${preview.columns.length} columns`} size="small" color="info" />
          <Typography variant="caption" color="text.secondary">
            Detected columns: {preview.columns.join(', ')}
          </Typography>
        </Stack>
        <TableContainer sx={{ maxHeight: 200 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {preview.columns.map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 700, fontSize: 12 }}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.sampleRows.map((row, idx) => (
                <TableRow key={idx}>
                  {preview.columns.map((col) => (
                    <TableCell key={col} sx={{ fontSize: 12 }}>
                      {row[col] != null ? String(row[col]).slice(0, 30) : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {preview.rowCount > preview.sampleRows.length && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Showing {preview.sampleRows.length} of {preview.rowCount} rows
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const ModelLabDatasetsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<ModelLabDataset | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<ModelLabDataset | null>(null);
  const [datasetForm, setDatasetForm] = useState<DatasetFormState>(initialDatasetForm);
  const [versionForm, setVersionForm] = useState<DatasetVersionFormState>(initialVersionForm);

  // Data input mode for create and version dialogs
  const [createInputMode, setCreateInputMode] = useState<DataInputMode>('file');
  const [versionInputMode, setVersionInputMode] = useState<DataInputMode>('file');

  // File upload state (create dialog)
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [createFilePreview, setCreateFilePreview] = useState<FilePreview | null>(null);
  const [createFileParsing, setCreateFileParsing] = useState(false);

  // File upload state (version dialog)
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionFilePreview, setVersionFilePreview] = useState<FilePreview | null>(null);
  const [versionFileParsing, setVersionFileParsing] = useState(false);

  // CSV Upload state (standalone per-dataset dialog)
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvDataset, setCsvDataset] = useState<ModelLabDataset | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvName, setCsvName] = useState('');
  const [csvDescription, setCsvDescription] = useState('');
  const [csvFilePreview, setCsvFilePreview] = useState<FilePreview | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Market Data Import state
  const [marketDialogOpen, setMarketDialogOpen] = useState(false);
  const [marketDataset, setMarketDataset] = useState<ModelLabDataset | null>(null);
  const [marketForm, setMarketForm] = useState({
    symbol: '',
    timeframe: 'D1',
    start: '',
    end: '',
    include_indicators: true,
    name: '',
  });

  // Create-dialog market form
  const [createMarketForm, setCreateMarketForm] = useState({
    symbol: '',
    timeframe: 'D1',
    start: '',
    end: '',
    include_indicators: true,
  });

  // Data Profile state
  const [profileVersionId, setProfileVersionId] = useState<number | null>(null);

  // View Details dialog state
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDataset, setViewDataset] = useState<ModelLabDataset | null>(null);
  const [viewVersionId, setViewVersionId] = useState<number | null>(null);

  // ── Dataset Merge state ──
  const [mergeOpen, setMergeOpen] = useState(false);
  type MergeStrategy = 'concat' | 'join_inner' | 'join_left' | 'join_right' | 'join_outer';
  interface MergeFormState {
    versionIds: number[];
    strategy: MergeStrategy;
    joinColumn: string;
    targetDatasetId: number | '';
    targetVersionName: string;
  }
  const [mergeForm, setMergeForm] = useState<MergeFormState>({
    versionIds: [],
    strategy: 'concat',
    joinColumn: '',
    targetDatasetId: '',
    targetVersionName: 'Merged',
  });

  // Query all versions for merge picker
  const allVersionsQuery = useQuery({
    queryKey: ['model-lab-all-versions'],
    queryFn: () => modelLabDatasetVersionApi.list({ page_size: 500 }),
    enabled: mergeOpen,
  });

  const allVersionsList: ModelLabDatasetVersion[] = useMemo(
    () => (allVersionsQuery.data as any)?.results ?? allVersionsQuery.data ?? [],
    [allVersionsQuery.data],
  );

  // Compute column options for the selected versions (intersection for join, union info)
  const mergeColumnOptions = useMemo(() => {
    if (mergeForm.versionIds.length === 0) return [];
    const selected = allVersionsList.filter((v) => mergeForm.versionIds.includes(v.id));
    if (selected.length === 0) return [];
    const colSets = selected.map((v) => {
      const recs = (v as any).records;
      if (Array.isArray(recs) && recs.length > 0) return Object.keys(recs[0]);
      if ((v as any).schema?.columns) return (v as any).schema.columns as string[];
      return [] as string[];
    });
    // intersection of columns
    const first = new Set(colSets[0]);
    for (let i = 1; i < colSets.length; i++) {
      const s = new Set(colSets[i]);
      first.forEach((c) => { if (!s.has(c)) first.delete(c); });
    }
    return Array.from(first);
  }, [mergeForm.versionIds, allVersionsList]);

  /* ── Parse file for preview ── */
  const handleFilePreview = useCallback((file: File, setPreview: (p: FilePreview | null) => void, setParsing: (b: boolean) => void) => {
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        if (file.name.endsWith('.json')) {
          const parsed = JSON.parse(text);
          const records = Array.isArray(parsed) ? parsed : [parsed];
          const columns = records.length > 0 ? Object.keys(records[0]) : [];
          setPreview({
            fileName: file.name,
            fileSize: file.size,
            columns,
            rowCount: records.length,
            sampleRows: records.slice(0, 5),
          });
        } else {
          const { columns, rows, totalRows } = parseCsvPreview(text);
          setPreview({
            fileName: file.name,
            fileSize: file.size,
            columns,
            rowCount: totalRows,
            sampleRows: rows,
          });
        }
      } catch {
        toast.error('Could not parse file. Ensure it is valid CSV or JSON.');
        setPreview(null);
      }
      setParsing(false);
    };
    reader.onerror = () => { setParsing(false); toast.error('Failed to read file.'); };
    reader.readAsText(file.slice(0, 1024 * 512)); // preview first 512KB
  }, []);

  const profileQuery = useQuery({
    queryKey: ['dataset-version-profile', profileVersionId],
    queryFn: () => (profileVersionId ? modelLabDatasetVersionApi.profile(profileVersionId) : Promise.resolve(null)),
    enabled: !!profileVersionId,
  });

  const datasetsQuery = useQuery({
    queryKey: ['model-lab-datasets', search],
    queryFn: () =>
      modelLabDatasetApi.list({
        search: search || undefined,
        page_size: 100,
      }),
  });

  const datasetVersionsQuery = useQuery({
    queryKey: ['model-lab-dataset-versions', selectedDataset?.id],
    queryFn: () => modelLabDatasetApi.getVersions(selectedDataset!.id),
    enabled: Boolean(selectedDataset),
  });

  const viewVersionsQuery = useQuery({
    queryKey: ['model-lab-dataset-versions-view', viewDataset?.id],
    queryFn: () => modelLabDatasetApi.getVersions(viewDataset!.id),
    enabled: Boolean(viewDataset && viewOpen),
  });

  const viewVersionDetailQuery = useQuery({
    queryKey: ['model-lab-dataset-version-detail', viewVersionId],
    queryFn: () => (viewVersionId ? modelLabDatasetVersionApi.get(viewVersionId) : Promise.resolve(null)),
    enabled: Boolean(viewVersionId && viewOpen),
  });

  const createDatasetMutation = useMutation({
    mutationFn: async (form: DatasetFormState) => {
      const datasetPayload: ModelLabDatasetCreate = {
        name: form.name,
        description: form.description,
        source_type: form.sourceType,
        schema: parseJsonInput(form.schemaJson, {}),
        tags: parseCommaList(form.tags),
      };

      const dataset = await modelLabDatasetApi.create(datasetPayload);

      // File upload mode
      if (createInputMode === 'file' && createFile) {
        await modelLabDatasetApi.uploadCsv(dataset.id, createFile, form.versionName || undefined);
        return dataset;
      }

      // Market data mode
      if (createInputMode === 'market') {
        await modelLabDatasetApi.importMarketData(dataset.id, {
          ...createMarketForm,
          name: form.versionName,
        });
        return dataset;
      }

      // JSON mode (original)
      const initialRecords = parseJsonInput<Array<Record<string, any>>>(
        form.recordsJson,
        []
      );
      if (!Array.isArray(initialRecords)) {
        throw new Error('Initial records JSON must be an array of objects.');
      }

      const versionPayload: ModelLabDatasetVersionCreate = {
        name: form.versionName,
        description: form.versionDescription,
        schema: parseJsonInput(form.schemaJson, {}),
        records: initialRecords,
        metadata: parseJsonInput(form.metadataJson, {}),
      };
      await modelLabDatasetApi.createVersion(dataset.id, versionPayload);

      return dataset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-dataset-versions'] });
      setCreateOpen(false);
      setDatasetForm(initialDatasetForm);
      setCreateFile(null);
      setCreateFilePreview(null);
      setCreateInputMode('file');
      toast.success('Dataset and initial version created.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create dataset.'));
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async (form: DatasetVersionFormState) => {
      if (!selectedDataset) {
        throw new Error('No dataset selected.');
      }

      // File upload mode
      if (versionInputMode === 'file' && versionFile) {
        return modelLabDatasetApi.uploadCsv(selectedDataset.id, versionFile, form.name || undefined);
      }

      // JSON mode
      const records = parseJsonInput<Array<Record<string, any>>>(
        form.recordsJson,
        []
      );
      if (!Array.isArray(records)) {
        throw new Error('Version records JSON must be an array of objects.');
      }

      return modelLabDatasetApi.createVersion(selectedDataset.id, {
        name: form.name,
        description: form.description,
        schema: parseJsonInput(form.schemaJson, {}),
        records,
        metadata: parseJsonInput(form.metadataJson, {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-dataset-versions'] });
      setVersionOpen(false);
      setVersionForm(initialVersionForm);
      setVersionFile(null);
      setVersionFilePreview(null);
      setVersionInputMode('file');
      toast.success('Dataset version created.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create dataset version.'));
    },
  });

  const deleteDatasetMutation = useMutation({
    mutationFn: (datasetId: number) => modelLabDatasetApi.delete(datasetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      setDatasetToDelete(null);
      toast.success('Dataset deleted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete dataset.'));
    },
  });

  const deleteVersionMutation = useMutation({
    mutationFn: (versionId: number) => modelLabDatasetVersionApi.delete(versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-dataset-versions'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      setProfileVersionId(null);
      toast.success('Version deleted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete version.'));
    },
  });

  const csvUploadMutation = useMutation({
    mutationFn: async () => {
      if (!csvDataset || !csvFile) throw new Error('Missing dataset or file');
      return modelLabDatasetApi.uploadCsv(csvDataset.id, csvFile, csvName || undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-dataset-versions'] });
      setCsvDialogOpen(false);
      setCsvFile(null);
      setCsvName('');
      toast.success('CSV uploaded as new version.');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'CSV upload failed.')),
  });

  const marketImportMutation = useMutation({
    mutationFn: async () => {
      if (!marketDataset) throw new Error('No dataset selected');
      return modelLabDatasetApi.importMarketData(marketDataset.id, marketForm);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-dataset-versions'] });
      setMarketDialogOpen(false);
      toast.success('Market data imported as new version.');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Market data import failed.')),
  });

  const mergeMutation = useMutation({
    mutationFn: async () => {
      if (mergeForm.versionIds.length < 2) throw new Error('Select at least 2 versions');
      if (!mergeForm.targetDatasetId) throw new Error('Select a target dataset');
      const isJoin = mergeForm.strategy.startsWith('join_');
      if (isJoin && !mergeForm.joinColumn) throw new Error('Join column is required');
      return modelLabDatasetApi.mergeVersions({
        version_ids: mergeForm.versionIds,
        strategy: mergeForm.strategy,
        join_column: isJoin ? mergeForm.joinColumn : undefined,
        target_dataset_id: mergeForm.targetDatasetId as number,
        target_version_name: mergeForm.targetVersionName || 'Merged',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-datasets'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-dataset-versions'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-all-versions'] });
      setMergeOpen(false);
      setMergeForm({ versionIds: [], strategy: 'concat', joinColumn: '', targetDatasetId: '', targetVersionName: 'Merged' });
      toast.success('Datasets merged into a new version.');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Merge failed.')),
  });

  const latestVersionCount = useMemo(
    () => datasetVersionsQuery.data?.length || 0,
    [datasetVersionsQuery.data]
  );

  if (datasetsQuery.error) {
    return <ErrorAlert error={datasetsQuery.error as Error} />;
  }

  if (datasetsQuery.isLoading) {
    return <LoadingSpinner message="Loading datasets..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Model Lab Datasets"
        breadcrumbs={[
          { label: 'Automation', to: '/ai' },
          { label: 'Model Lab', to: '/ai/ml' },
          { label: 'Datasets' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<MergeIcon />}
              onClick={() => setMergeOpen(true)}
            >
              Merge Datasets
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Add Dataset
            </Button>
          </Stack>
        }
      />

      <Stack spacing={2}>
        <Alert severity="info">
          Create datasets with an initial version so they are ready for training
          jobs immediately. Versions stay attached to the dataset as immutable
          checkpoints.
        </Alert>

        {selectedDataset && (
          <Alert severity="success">
            {selectedDataset.name} has {latestVersionCount} version
            {latestVersionCount === 1 ? '' : 's'} loaded in this session.
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Latest Version</TableCell>
                <TableCell>Version Count</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(datasetsQuery.data?.results || []).map((dataset) => (
                <TableRow key={dataset.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{dataset.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dataset.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>{dataset.source_type}</TableCell>
                  <TableCell>v{dataset.latest_version_number || 0}</TableCell>
                  <TableCell>{dataset.versions_count}</TableCell>
                  <TableCell>
                    {formatStoredDateTime(dataset.updated_at || dataset.created_at, undefined, '-')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details & Data">
                      <IconButton
                        onClick={() => {
                          setViewDataset(dataset);
                          setViewVersionId(null);
                          setViewOpen(true);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Upload CSV">
                      <IconButton
                        color="secondary"
                        onClick={() => {
                          setCsvDataset(dataset);
                          setCsvDialogOpen(true);
                        }}
                      >
                        <UploadFileIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Import Market Data">
                      <IconButton
                        color="info"
                        onClick={() => {
                          setMarketDataset(dataset);
                          setMarketDialogOpen(true);
                        }}
                      >
                        <ShowChartIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Version">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setSelectedDataset(dataset);
                          setVersionForm(initialVersionForm);
                          setVersionOpen(true);
                        }}
                      >
                        <LibraryAddIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      color="error"
                      onClick={() => setDatasetToDelete(dataset)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(datasetsQuery.data?.results || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No datasets found. Create one to start the training workflow.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Dataset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Dataset name"
              value={datasetForm.name}
              onChange={(event) =>
                setDatasetForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              required
              placeholder="e.g. EURUSD Daily Features"
            />
            <TextField
              label="Description"
              value={datasetForm.description}
              onChange={(event) =>
                setDatasetForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={2}
              placeholder="Describe the purpose and contents of this dataset"
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Tags"
                helperText="Comma separated"
                value={datasetForm.tags}
                onChange={(event) =>
                  setDatasetForm((current) => ({
                    ...current,
                    tags: event.target.value,
                  }))
                }
                fullWidth
                placeholder="e.g. forex, daily, features"
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle1" fontWeight={600}>Initial Version Data</Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Version name"
                placeholder="e.g. Seed v1, Initial import"
                helperText="Short label identifying this version"
                value={datasetForm.versionName}
                onChange={(event) =>
                  setDatasetForm((current) => ({
                    ...current,
                    versionName: event.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Version description"
                placeholder="What changed or what data does this version contain?"
                helperText="Optional notes about this version"
                value={datasetForm.versionDescription}
                onChange={(event) =>
                  setDatasetForm((current) => ({
                    ...current,
                    versionDescription: event.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>

            <Tabs
              value={createInputMode}
              onChange={(_, v) => {
                setCreateInputMode(v);
                // Auto-sync source type based on tab
                const sourceMap: Record<DataInputMode, ModelLabDatasetSourceType> = {
                  file: 'FILE',
                  json: 'MANUAL',
                  market: 'API',
                };
                setDatasetForm((current) => ({ ...current, sourceType: sourceMap[v as DataInputMode] || 'MANUAL' }));
              }}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<UploadFileIcon />} iconPosition="start" label="Upload File" value="file" />
              <Tab icon={<DataObjectIcon />} iconPosition="start" label="Paste JSON" value="json" />
              <Tab icon={<ShowChartIcon />} iconPosition="start" label="Market Data" value="market" />
            </Tabs>

            {/* ── File Upload Tab ── */}
            {createInputMode === 'file' && (
              <Stack spacing={2}>
                <Alert severity="info" variant="outlined">
                  Upload a CSV or JSON file (max 10 MB). The file will be parsed and stored as a new dataset version.
                  Supported formats: <strong>.csv</strong>, <strong>.json</strong> (array of objects).
                </Alert>
                <DropZone
                  file={createFile}
                  accept=".csv,.json"
                  label="Drop CSV or JSON file here"
                  onFile={(f) => {
                    if (f.size > 10 * 1024 * 1024) {
                      toast.error('File exceeds 10 MB limit. Please use a smaller file.');
                      return;
                    }
                    setCreateFile(f);
                    handleFilePreview(f, setCreateFilePreview, setCreateFileParsing);
                  }}
                />
                <FilePreviewTable preview={createFilePreview} loading={createFileParsing} />
              </Stack>
            )}

            {/* ── JSON Tab ── */}
            {createInputMode === 'json' && (
              <Stack spacing={2}>
                <TextField
                  label="Schema JSON (optional)"
                  value={datasetForm.schemaJson}
                  onChange={(event) =>
                    setDatasetForm((current) => ({
                      ...current,
                      schemaJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={3}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  helperText="Define column types and constraints (optional)"
                />
                <TextField
                  label="Records JSON"
                  value={datasetForm.recordsJson}
                  onChange={(event) =>
                    setDatasetForm((current) => ({
                      ...current,
                      recordsJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={8}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  helperText='Array of objects, e.g. [{"feature_1": 1.0, "target": 0}]'
                />
                <TextField
                  label="Metadata JSON (optional)"
                  value={datasetForm.metadataJson}
                  onChange={(event) =>
                    setDatasetForm((current) => ({
                      ...current,
                      metadataJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  helperText="Optional key-value metadata for this version"
                />
              </Stack>
            )}

            {/* ── Market Data Tab ── */}
            {createInputMode === 'market' && (
              <Stack spacing={2}>
                <Alert severity="info" variant="outlined">
                  Import OHLCV data directly from the data warehouse. Technical indicators can be
                  included automatically.
                </Alert>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Symbol"
                    value={createMarketForm.symbol}
                    onChange={(e) => setCreateMarketForm({ ...createMarketForm, symbol: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g. EURUSD, AAPL"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Timeframe"
                    value={createMarketForm.timeframe}
                    onChange={(e) => setCreateMarketForm({ ...createMarketForm, timeframe: e.target.value })}
                    fullWidth
                  >
                    {['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'].map((tf) => (
                      <MenuItem key={tf} value={tf}>{tf}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={createMarketForm.start}
                    onChange={(e) => setCreateMarketForm({ ...createMarketForm, start: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={createMarketForm.end}
                    onChange={(e) => setCreateMarketForm({ ...createMarketForm, end: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    error={!!(createMarketForm.start && createMarketForm.end && createMarketForm.end < createMarketForm.start)}
                    helperText={createMarketForm.start && createMarketForm.end && createMarketForm.end < createMarketForm.start ? 'End date must be after start date' : ''}
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={createMarketForm.include_indicators}
                      onChange={(e) =>
                        setCreateMarketForm({ ...createMarketForm, include_indicators: e.target.checked })
                      }
                    />
                  }
                  label="Include technical indicators (SMA, EMA, RSI, MACD, Bollinger Bands)"
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createDatasetMutation.mutate(datasetForm)}
            disabled={
              !datasetForm.name ||
              createDatasetMutation.isPending ||
              (createInputMode === 'file' && !createFile) ||
              (createInputMode === 'market' && !createMarketForm.symbol)
            }
          >
            {createDatasetMutation.isPending ? 'Creating...' : 'Create Dataset'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={versionOpen}
        onClose={() => setVersionOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Dataset Version {selectedDataset ? `— ${selectedDataset.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Version name"
                placeholder="e.g. v2-cleaned, rebalanced"
                helperText="Short label identifying this version revision"
                value={versionForm.name}
                onChange={(event) =>
                  setVersionForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Description"
                placeholder="What changed from the previous version?"
                helperText="Optional notes about this version"
                value={versionForm.description}
                onChange={(event) =>
                  setVersionForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>

            <Tabs
              value={versionInputMode}
              onChange={(_, v) => setVersionInputMode(v)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<UploadFileIcon />} iconPosition="start" label="Upload File" value="file" />
              <Tab icon={<DataObjectIcon />} iconPosition="start" label="Paste JSON" value="json" />
            </Tabs>

            {versionInputMode === 'file' && (
              <Stack spacing={2}>
                <DropZone
                  file={versionFile}
                  accept=".csv,.json"
                  label="Drop CSV or JSON file here"
                  onFile={(f) => {
                    if (f.size > 10 * 1024 * 1024) {
                      toast.error('File exceeds 10 MB limit. Please use a smaller file.');
                      return;
                    }
                    setVersionFile(f);
                    handleFilePreview(f, setVersionFilePreview, setVersionFileParsing);
                  }}
                />
                <FilePreviewTable preview={versionFilePreview} loading={versionFileParsing} />
              </Stack>
            )}

            {versionInputMode === 'json' && (
              <Stack spacing={2}>
                <TextField
                  label="Schema JSON (optional)"
                  value={versionForm.schemaJson}
                  onChange={(event) =>
                    setVersionForm((current) => ({
                      ...current,
                      schemaJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={3}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />
                <TextField
                  label="Records JSON"
                  value={versionForm.recordsJson}
                  onChange={(event) =>
                    setVersionForm((current) => ({
                      ...current,
                      recordsJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={8}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  helperText='Array of objects, e.g. [{"feature_1": 1.0, "target": 0}]'
                />
                <TextField
                  label="Metadata JSON (optional)"
                  value={versionForm.metadataJson}
                  onChange={(event) =>
                    setVersionForm((current) => ({
                      ...current,
                      metadataJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />
              </Stack>
            )}

            {selectedDataset && datasetVersionsQuery.data && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Existing versions: {datasetVersionsQuery.data.map((item) => item.name).join(', ') || 'none'}.
                </Typography>
                {datasetVersionsQuery.data.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Data Profile</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {datasetVersionsQuery.data.map((v) => (
                        <Chip
                          key={v.id}
                          label={`Profile v${v.version_number}`}
                          icon={<BarChartIcon />}
                          onClick={() => setProfileVersionId(profileVersionId === v.id ? null : v.id)}
                          onDelete={() => {
                            if (confirm(`Delete version v${v.version_number} "${v.name}"?`)) {
                              deleteVersionMutation.mutate(v.id);
                            }
                          }}
                          deleteIcon={<DeleteIcon fontSize="small" />}
                          color={profileVersionId === v.id ? 'primary' : 'default'}
                          variant={profileVersionId === v.id ? 'filled' : 'outlined'}
                          size="small"
                        />
                      ))}
                    </Stack>
                    {profileVersionId && (
                      <Card variant="outlined" sx={{ mt: 2 }}>
                        <CardContent>
                          {profileQuery.isLoading ? (
                            <CircularProgress size={20} />
                          ) : profileQuery.data ? (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                {profileQuery.data.row_count} rows, {profileQuery.data.column_count} columns
                              </Typography>

                              {/* Statistics Table */}
                              <TableContainer sx={{ maxHeight: 300 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Column</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell>Nulls</TableCell>
                                      <TableCell>Mean</TableCell>
                                      <TableCell>Std</TableCell>
                                      <TableCell>Min</TableCell>
                                      <TableCell>Max</TableCell>
                                      <TableCell>Unique</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {profileQuery.data.columns.map((col) => (
                                      <TableRow key={col.name}>
                                        <TableCell><strong>{col.name}</strong></TableCell>
                                        <TableCell>{col.dtype}</TableCell>
                                        <TableCell>{col.null_count}</TableCell>
                                        <TableCell>{col.mean != null ? col.mean.toFixed(2) : '—'}</TableCell>
                                        <TableCell>{col.std != null ? col.std.toFixed(2) : '—'}</TableCell>
                                        <TableCell>{col.min != null ? col.min : '—'}</TableCell>
                                        <TableCell>{col.max != null ? col.max : '—'}</TableCell>
                                        <TableCell>{col.unique_count != null ? col.unique_count : '—'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>

                              <Divider sx={{ my: 2 }} />

                              {/* Box Plot Summary */}
                              <BoxPlotSummary columns={profileQuery.data.columns} />

                              {/* Correlation Heatmap */}
                              {profileQuery.data.correlation && (
                                <Box sx={{ mt: 2 }}>
                                  <CorrelationHeatmap
                                    columns={profileQuery.data.correlation.columns}
                                    matrix={profileQuery.data.correlation.matrix}
                                  />
                                </Box>
                              )}

                              {/* Distribution Charts */}
                              {profileQuery.data.columns.some((c) => c.histogram || c.value_counts) && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>Column Distributions</Typography>
                                  <Grid container spacing={2}>
                                    {profileQuery.data.columns.map((col) => {
                                      if (col.histogram) {
                                        return (
                                          <Grid item xs={12} md={6} key={col.name}>
                                            <NumericDistribution histogram={col.histogram} title={col.name} />
                                          </Grid>
                                        );
                                      }
                                      if (col.value_counts && Object.keys(col.value_counts).length > 0) {
                                        return (
                                          <Grid item xs={12} md={6} key={col.name}>
                                            <CategoricalDistribution valueCounts={col.value_counts} title={col.name} />
                                          </Grid>
                                        );
                                      }
                                      return null;
                                    })}
                                  </Grid>
                                </Box>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">No profile data</Typography>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createVersionMutation.mutate(versionForm)}
            disabled={
              !versionForm.name ||
              createVersionMutation.isPending ||
              (versionInputMode === 'file' && !versionFile)
            }
          >
            {createVersionMutation.isPending ? 'Creating...' : 'Add Version'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(datasetToDelete)}
        title="Delete dataset"
        message={`Delete ${datasetToDelete?.name}? This removes all of its versions and training references.`}
        confirmColor="error"
        confirmText="Delete"
        onCancel={() => setDatasetToDelete(null)}
        onConfirm={() => {
          if (datasetToDelete) {
            deleteDatasetMutation.mutate(datasetToDelete.id);
          }
        }}
      />

      {/* CSV Upload Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload CSV — {csvDataset?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Version name (optional)"
              value={csvName}
              onChange={(e) => setCsvName(e.target.value)}
              fullWidth
              placeholder="e.g. v2 updated features"
            />
            <TextField
              label="Description (optional)"
              value={csvDescription}
              onChange={(e) => setCsvDescription(e.target.value)}
              fullWidth
              placeholder="What changed in this version?"
            />
            <DropZone
              file={csvFile}
              accept=".csv"
              label="Drop CSV file here"
              onFile={(f) => {
                if (f.size > 10 * 1024 * 1024) {
                  toast.error('File exceeds 10 MB limit. Please use a smaller file.');
                  return;
                }
                setCsvFile(f);
                handleFilePreview(f, setCsvFilePreview, () => {});
              }}
            />
            <FilePreviewTable preview={csvFilePreview} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCsvDialogOpen(false); setCsvFile(null); setCsvFilePreview(null); setCsvDescription(''); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!csvFile || csvUploadMutation.isPending}
            onClick={() => csvUploadMutation.mutate()}
          >
            {csvUploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Market Data Import Dialog */}
      <Dialog open={marketDialogOpen} onClose={() => setMarketDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Market Data — {marketDataset?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" variant="outlined">
              Pull OHLCV bars from the data warehouse. A new dataset version will be created
              with the imported data.
            </Alert>
            <TextField
              label="Symbol"
              value={marketForm.symbol}
              onChange={(e) => setMarketForm({ ...marketForm, symbol: e.target.value.toUpperCase() })}
              required
              placeholder="e.g. EURUSD, AAPL"
              fullWidth
            />
            <TextField
              select
              label="Timeframe"
              value={marketForm.timeframe}
              onChange={(e) => setMarketForm({ ...marketForm, timeframe: e.target.value })}
              fullWidth
            >
              {['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'].map((tf) => (
                <MenuItem key={tf} value={tf}>{tf}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={marketForm.start}
                onChange={(e) => setMarketForm({ ...marketForm, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={marketForm.end}
                onChange={(e) => setMarketForm({ ...marketForm, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={!!(marketForm.start && marketForm.end && marketForm.end < marketForm.start)}
                helperText={marketForm.start && marketForm.end && marketForm.end < marketForm.start ? 'End date must be after start date' : ''}
              />
            </Stack>
            <TextField
              label="Version name (optional)"
              value={marketForm.name}
              onChange={(e) => setMarketForm({ ...marketForm, name: e.target.value })}
              fullWidth
              placeholder="e.g. EURUSD D1 2024"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={marketForm.include_indicators}
                  onChange={(e) =>
                    setMarketForm({ ...marketForm, include_indicators: e.target.checked })
                  }
                />
              }
              label="Include technical indicators (SMA, EMA, RSI, MACD, Bollinger Bands)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarketDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!marketForm.symbol || marketImportMutation.isPending}
            onClick={() => marketImportMutation.mutate()}
          >
            {marketImportMutation.isPending ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Merge Datasets Dialog ── */}
      <Dialog open={mergeOpen} onClose={() => setMergeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Merge Dataset Versions</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Alert severity="info" variant="outlined">
              Combine two or more dataset versions into a single new version. Use
              <strong> Concatenate </strong> to stack rows vertically, or <strong> Join </strong> to
              merge columns on a shared key column.
            </Alert>

            {/* Version picker */}
            <Autocomplete
              multiple
              options={allVersionsList}
              getOptionLabel={(v) =>
                `${(v as any).dataset_name ?? `Dataset #${v.dataset}`} — ${v.name} (v${v.version_number}, ${v.record_count ?? '?'} rows)`
              }
              value={allVersionsList.filter((v) => mergeForm.versionIds.includes(v.id))}
              onChange={(_, vals) =>
                setMergeForm((f) => ({ ...f, versionIds: vals.map((v) => v.id) }))
              }
              loading={allVersionsQuery.isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select versions to merge"
                  placeholder="Search by dataset or version name..."
                  helperText={`${mergeForm.versionIds.length} selected — minimum 2 required`}
                />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />

            {/* Strategy */}
            <TextField
              select
              label="Merge Strategy"
              value={mergeForm.strategy}
              onChange={(e) =>
                setMergeForm((f) => ({ ...f, strategy: e.target.value as MergeStrategy }))
              }
              fullWidth
              helperText={
                mergeForm.strategy === 'concat'
                  ? 'Stack all rows vertically. Columns are unioned; missing values become null.'
                  : `Pandas-style ${mergeForm.strategy.replace('join_', '')} join on a shared key column.`
              }
            >
              <MenuItem value="concat">Concatenate (stack rows)</MenuItem>
              <MenuItem value="join_inner">Inner Join (only matching keys)</MenuItem>
              <MenuItem value="join_left">Left Join (keep all left rows)</MenuItem>
              <MenuItem value="join_right">Right Join (keep all right rows)</MenuItem>
              <MenuItem value="join_outer">Outer Join (keep all rows)</MenuItem>
            </TextField>

            {/* Join column (only for join strategies) */}
            {mergeForm.strategy.startsWith('join_') && (
              <Autocomplete
                freeSolo
                options={mergeColumnOptions}
                value={mergeForm.joinColumn}
                onInputChange={(_, v) => setMergeForm((f) => ({ ...f, joinColumn: v }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Join Key Column"
                    required
                    placeholder="e.g. date, timestamp, symbol"
                    helperText={
                      mergeColumnOptions.length > 0
                        ? `Columns shared by all selected versions: ${mergeColumnOptions.join(', ')}`
                        : 'Select versions first to see common columns'
                    }
                  />
                )}
              />
            )}

            <Divider />
            <Typography variant="subtitle2">Save Result To</Typography>

            {/* Target dataset */}
            <TextField
              select
              label="Target Dataset"
              value={mergeForm.targetDatasetId}
              onChange={(e) =>
                setMergeForm((f) => ({ ...f, targetDatasetId: Number(e.target.value) }))
              }
              fullWidth
              required
              helperText="The merged data will be saved as a new version of this dataset"
            >
              {(datasetsQuery.data?.results || []).map((ds) => (
                <MenuItem key={ds.id} value={ds.id}>
                  {ds.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="New Version Name"
              value={mergeForm.targetVersionName}
              onChange={(e) =>
                setMergeForm((f) => ({ ...f, targetVersionName: e.target.value }))
              }
              fullWidth
              placeholder="e.g. Merged v1"
            />

            {/* Summary chips */}
            {mergeForm.versionIds.length >= 2 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={`${mergeForm.versionIds.length} versions`}
                  size="small"
                  color="primary"
                />
                <Chip
                  label={mergeForm.strategy === 'concat' ? 'Concatenate' : mergeForm.strategy.replace('join_', '').toUpperCase() + ' JOIN'}
                  size="small"
                  color="secondary"
                />
                {mergeForm.strategy.startsWith('join_') && mergeForm.joinColumn && (
                  <Chip label={`ON ${mergeForm.joinColumn}`} size="small" variant="outlined" />
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              mergeForm.versionIds.length < 2 ||
              !mergeForm.targetDatasetId ||
              (mergeForm.strategy.startsWith('join_') && !mergeForm.joinColumn) ||
              mergeMutation.isPending
            }
            onClick={() => mergeMutation.mutate()}
          >
            {mergeMutation.isPending ? 'Merging...' : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog — dataset config + versions + data preview */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {viewDataset ? `Dataset: ${viewDataset.name}` : 'Dataset Details'}
        </DialogTitle>
        <DialogContent dividers>
          {viewDataset && (
            <Stack spacing={2}>
              {/* Configuration */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Configuration</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="body2">{viewDataset.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Owner</Typography>
                      <Typography variant="body2">{viewDataset.user_username}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Source Type</Typography>
                      <Typography variant="body2">{viewDataset.source_type}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Active</Typography>
                      <Typography variant="body2">{viewDataset.is_active ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{viewDataset.description || '—'}</Typography>
                    </Grid>
                    {viewDataset.tags?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Tags</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {viewDataset.tags.map((t) => (
                            <Chip key={t} label={t} size="small" />
                          ))}
                        </Stack>
                      </Grid>
                    )}
                    {viewDataset.source_config && Object.keys(viewDataset.source_config).length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Source Config</Typography>
                        <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: 180, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>
                            {JSON.stringify(viewDataset.source_config, null, 2)}
                          </pre>
                        </Paper>
                      </Grid>
                    )}
                    {viewDataset.schema && Object.keys(viewDataset.schema).length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Schema</Typography>
                        <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: 180, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>
                            {JSON.stringify(viewDataset.schema, null, 2)}
                          </pre>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Versions list */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Versions ({viewVersionsQuery.data?.length || 0})
                  </Typography>
                  {viewVersionsQuery.isLoading ? (
                    <CircularProgress size={20} />
                  ) : (viewVersionsQuery.data?.length || 0) === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No versions yet. Use "Add Version", "Upload CSV", or "Import Market Data" to add data.
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {viewVersionsQuery.data!.map((v) => (
                        <Chip
                          key={v.id}
                          label={`v${v.version_number} — ${v.name} (${v.record_count} rows)`}
                          color={viewVersionId === v.id ? 'primary' : 'default'}
                          variant={viewVersionId === v.id ? 'filled' : 'outlined'}
                          onClick={() => setViewVersionId(v.id)}
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              {/* Selected version data preview */}
              {viewVersionId && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Data Preview</Typography>
                    {viewVersionDetailQuery.isLoading ? (
                      <CircularProgress size={20} />
                    ) : viewVersionDetailQuery.data ? (
                      (() => {
                        const v = viewVersionDetailQuery.data as ModelLabDatasetVersion;
                        const records = Array.isArray(v.records) ? v.records : [];
                        const sample = records.slice(0, 50);
                        const columns = sample.length > 0 ? Object.keys(sample[0]) : [];
                        return (
                          <>
                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                              <Chip label={`${v.record_count} rows`} size="small" />
                              <Chip label={`${columns.length} columns`} size="small" />
                              <Chip
                                label="View Profile"
                                icon={<BarChartIcon />}
                                size="small"
                                onClick={() => setProfileVersionId(v.id)}
                              />
                            </Stack>
                            {v.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {v.description}
                              </Typography>
                            )}
                            {columns.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No inline records available. This version may use an external source.
                              </Typography>
                            ) : (
                              <TableContainer sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      {columns.map((c) => (
                                        <TableCell key={c}><strong>{c}</strong></TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {sample.map((row, i) => (
                                      <TableRow key={i}>
                                        {columns.map((c) => (
                                          <TableCell key={c}>
                                            {row[c] === null || row[c] === undefined
                                              ? '—'
                                              : typeof row[c] === 'object'
                                                ? JSON.stringify(row[c])
                                                : String(row[c])}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                            {records.length > sample.length && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Showing first {sample.length} of {records.length} rows.
                              </Typography>
                            )}
                            {v.metadata && Object.keys(v.metadata).length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary">Metadata</Typography>
                                <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: 140, overflow: 'auto' }}>
                                  <pre style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>
                                    {JSON.stringify(v.metadata, null, 2)}
                                  </pre>
                                </Paper>
                              </Box>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="text.secondary">No data.</Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelLabDatasetsPage;